// What the client decrypts of a message with its key (MCK): the parts the sender encrypted.
// Transcript, segments, mood and topics come in plain text from the server (it holds the keys).

import type { MessageResponse } from "../api/contract.gen.ts";
import { ContentKind, decrypt } from "../crypto/contentCipher.ts";
import { decodeUtf8, fromBase64 } from "./bytes.ts";

export interface Place {
  lat: number;
  lon: number;
  acc?: number;
  label?: string;
  place?: boolean;
  live?: boolean;
}

export interface Gif {
  provider: string;
  slug: string;
  title: string;
  url: string;
  preview?: string;
  still?: string;
  w: number;
  h: number;
}

export interface LinkPreview {
  url: string;
  title: string;
  description?: string;
  siteName?: string;
  /** A base64 JPEG. */
  image?: string;
}

export interface Content {
  /** The text of a text message, or the caption of an image, video or file. */
  text?: string;
  place?: Place;
  gif?: Gif;
  linkPreview?: LinkPreview;
  fileName?: string;
  /** One value 0..255 per started 100 ms of a voice message. */
  waveform?: Uint8Array;
  /** The content could not be decrypted (no key, or it does not open with it). */
  broken?: boolean;
}

/** Decrypts the encrypted fields of [message] with its key; never throws. */
export async function decryptContent(message: MessageResponse, mck: Uint8Array<ArrayBuffer> | undefined): Promise<Content> {
  if (message.deleted) return {};
  if (!mck) return { broken: true };
  const content: Content = {};
  const open = async (kind: ContentKind, base64: string | null | undefined) => {
    if (!base64) return undefined;
    const blob = fromBase64(base64);
    return blob.length === 0 ? undefined : decrypt(kind, blob, mck);
  };
  try {
    if (message.kind === "location") {
      const bytes = await open(ContentKind.LOCATION, message.body);
      if (bytes) content.place = JSON.parse(decodeUtf8(bytes)) as Place;
    } else if (message.kind === "gif") {
      const bytes = await open(ContentKind.GIF, message.body);
      if (bytes) content.gif = JSON.parse(decodeUtf8(bytes)) as Gif;
    } else {
      const bytes = await open(ContentKind.TEXT, message.body);
      if (bytes) content.text = decodeUtf8(bytes);
    }
    const waveform = await open(ContentKind.WAVEFORM, message.waveform);
    if (waveform) content.waveform = waveform;
    const preview = await open(ContentKind.LINK_PREVIEW, message.link_preview);
    if (preview) content.linkPreview = JSON.parse(decodeUtf8(preview)) as LinkPreview;
    const fileName = await open(ContentKind.FILE_NAME, message.media?.file_name);
    if (fileName) content.fileName = decodeUtf8(fileName);
  } catch {
    content.broken = true;
  }
  return content;
}
